import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Progress,
} from "reactstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";

function ListadoProyectosSoporte() {
  const { user } = useContext(AuthContext);
  const URL_SUPPORT_PROJECTS = process.env.REACT_APP_URL_SUPPORT_PROJECTS;
  const URL_SUPPORT_TICKETS = process.env.REACT_APP_URL_SUPPORT_TICKETS;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;

  const [proyectos, setProyectos] = useState([]);
  const [ticketsCounts, setTicketsCounts] = useState({});
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Pagination
  const [comments, setComments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  const headers = [
    { name: "Cliente", field: "clientName", sortable: true },
    { name: "Repos", field: "repos", sortable: false },
    { name: "Base de Datos", field: "database", sortable: false },
    { name: "Grupos WA", field: "whatsappGroups", sortable: false },
    { name: "Knowledge Base", field: "knowledgeBase", sortable: false },
    { name: "IA", field: "ai", sortable: false },
    { name: "Tickets", field: "tickets", sortable: false },
    { name: "Acciones", field: "actions", sortable: false },
  ];

  useEffect(() => {
    getProyectos();
  }, []);

  const getProyectos = () => {
    showLoader();
    axios
      .get(URL_SUPPORT_PROJECTS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setProyectos(response.data);
        setComments(response.data);
        hideLoader();
        
        // Cargar conteo de tickets para cada proyecto
        response.data.forEach(proyecto => {
          getTicketsCount(proyecto._id);
        });
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  };

  const getTicketsCount = (projectId) => {
    axios
      .get(`${URL_SUPPORT_TICKETS}Project/${projectId}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        const tickets = response.data;
        const openTickets = tickets.filter(t => 
          t.status !== 'resolved' && t.status !== 'closed'
        ).length;
        const criticalTickets = tickets.filter(t => 
          t.priority === 'critical' && t.status !== 'resolved' && t.status !== 'closed'
        ).length;
        
        setTicketsCounts(prev => ({
          ...prev,
          [projectId]: { open: openTickets, critical: criticalTickets }
        }));
      })
      .catch((err) => console.log(err));
  };

  const toggleAI = (id, currentStatus) => {
    Swal.fire({
      title: currentStatus ? '¿Desactivar IA?' : '¿Activar IA?',
      text: currentStatus 
        ? "La IA dejará de responder automáticamente" 
        : "La IA comenzará a responder automáticamente",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: currentStatus ? 'Desactivar' : 'Activar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .patch(
            `${URL_SUPPORT_PROJECTS}ToggleAI/${id}`,
            {},
            {
              headers: {
                Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
              },
            }
          )
          .then((response) => {
            Swal.fire(
              'Actualizado',
              `IA ${!currentStatus ? 'activada' : 'desactivada'} correctamente`,
              'success'
            );
            getProyectos();
          })
          .catch((err) => {
            console.log(err);
            Swal.fire('Error', 'No se pudo actualizar', 'error');
          });
      }
    });
  };

  const deleteProyecto = (id) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "El proyecto se marcará como inactivo",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${URL_SUPPORT_PROJECTS}/${id}`, {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          })
          .then((response) => {
            Swal.fire('Eliminado', 'Proyecto eliminado correctamente', 'success');
            getProyectos();
          })
          .catch((err) => {
            console.log(err);
            Swal.fire('Error', 'No se pudo eliminar', 'error');
          });
      }
    });
  };

  const commentsData = useMemo(() => {
    let computedComments = comments;

    if (search) {
      computedComments = computedComments.filter(
        (comment) =>
          comment.clientName.toLowerCase().includes(search.toLowerCase()) ||
          comment.slug.toLowerCase().includes(search.toLowerCase())
      );
    }

    setTotalItems(computedComments.length);

    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computedComments = computedComments.sort(
        (a, b) => reversed * a[sorting.field].localeCompare(b[sorting.field])
      );
    }

    return computedComments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting]);

  return (
    <>
      <Header />
      {loader}
      {user.menu_soporte ? (
        <div className="container-fluid">
          <br />
          <Row>
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <Row>
                    <div className="col-md-6">
                      <h3>Proyectos de Soporte</h3>
                    </div>
                    <div className="col-md-6" align="right">
                      <Button
                        color="success"
                        href="/ProyectosSoporteCreate"
                      >
                        <i className="fas fa-plus"></i> Agregar Proyecto
                      </Button>
                    </div>
                  </Row>
                </div>
                <div className="card-body">
                  <Row>
                    <div className="col-md-12 mb-3">
                      <Search
                        onSearch={(value) => {
                          setSearch(value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </Row>
                  <Table striped hover responsive>
                    <TableHeader
                      headers={headers}
                      onSorting={(field, order) => setSorting({ field, order })}
                    />
                    <tbody>
                      {commentsData.map((proyecto) => {
                        const ticketInfo = ticketsCounts[proyecto._id] || { open: 0, critical: 0 };
                        return (
                          <tr key={proyecto._id}>
                            <td>
                              <strong>{proyecto.clientName}</strong>
                              <br />
                              <small className="text-muted">{proyecto.slug}</small>
                            </td>
                            <td>
                              {proyecto.repos?.back?.name && (
                                <div>
                                  <Badge color="info">Back</Badge>{" "}
                                  <small>{proyecto.repos.back.name}</small>
                                </div>
                              )}
                              {proyecto.repos?.front?.name && (
                                <div>
                                  <Badge color="primary">Front</Badge>{" "}
                                  <small>{proyecto.repos.front.name}</small>
                                </div>
                              )}
                            </td>
                            <td>
                              <div>
                                <Badge color="secondary">{proyecto.database?.cluster}</Badge>
                              </div>
                              <small>{proyecto.database?.dbName}</small>
                            </td>
                            <td>
                              <Badge color="success">
                                {proyecto.whatsappGroups?.length || 0} grupos
                              </Badge>
                            </td>
                            <td>
                              <div>
                                <Progress 
                                  value={proyecto.knowledgeBase?.completeness || 0}
                                  color={
                                    (proyecto.knowledgeBase?.completeness || 0) < 30 ? 'danger' :
                                    (proyecto.knowledgeBase?.completeness || 0) < 70 ? 'warning' : 'success'
                                  }
                                >
                                  {proyecto.knowledgeBase?.completeness || 0}%
                                </Progress>
                              </div>
                            </td>
                            <td>
                              <Button
                                color={proyecto.aiConfig?.enabled ? "success" : "secondary"}
                                size="sm"
                                onClick={() => toggleAI(proyecto._id, proyecto.aiConfig?.enabled)}
                              >
                                {proyecto.aiConfig?.enabled ? (
                                  <><i className="fas fa-toggle-on"></i> ON</>
                                ) : (
                                  <><i className="fas fa-toggle-off"></i> OFF</>
                                )}
                              </Button>
                            </td>
                            <td>
                              {ticketInfo.open > 0 ? (
                                <>
                                  <Badge color={ticketInfo.critical > 0 ? "danger" : "warning"}>
                                    {ticketInfo.open} abiertos
                                  </Badge>
                                  {ticketInfo.critical > 0 && (
                                    <><br /><Badge color="danger">{ticketInfo.critical} críticos</Badge></>
                                  )}
                                </>
                              ) : (
                                <Badge color="light">0 abiertos</Badge>
                              )}
                            </td>
                            <td>
                              <Button
                                color="primary"
                                size="sm"
                                href={`/ProyectosSoporteEdit/${proyecto._id}`}
                              >
                                <i className="fas fa-edit"></i>
                              </Button>{" "}
                              <Button
                                color="info"
                                size="sm"
                                href={`/ListadoTickets?projectId=${proyecto._id}`}
                              >
                                <i className="fas fa-ticket-alt"></i>
                              </Button>{" "}
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => deleteProyecto(proyecto._id)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <Row>
                    <div className="col-md-12">
                      <Pagination
                        total={totalItems}
                        itemsPerPage={ITEMS_PER_PAGE}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    </div>
                  </Row>
                </div>
              </div>
            </div>
          </Row>
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoProyectosSoporte;
